// frontend/src/pages/Reports.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { reportsApi } from "../api/client";

function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null;
    return n;
  }
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

function splitNameParts({ first, last, full }) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (f || l) {
    return {
      first: f,
      last: l || (full ? String(full).trim().split(/\s+/).slice(-1)[0] || "" : ""),
    };
  }
  const name = (full || "").trim();
  if (!name) return { first: "", last: "" };
  const parts = name.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

const normHouse = (s) => String(s ?? "").toLowerCase().replace(/\s+/g, " ").trim();

// Fixed house list for radio group
const HOUSE_LIST = ["JUNBI", "LAULIMA", "SAVANT", "TAFAKARI", "UMUSA"];
const TOP_N = 10;

// Preserve leading zeros; attempt to strip non-digits if present
function normalizeLunchNumber(value) {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return raw; // already digits, keep as-is
  const digits = raw.replace(/\D+/g, "");
  return digits || raw; // prefer digits; if none, keep the raw string
}

export default function Reports() {
  const navigate = useNavigate();

  // UI state
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [pointsMode, setPointsMode] = useState("all"); // 'all' | 'top' | 'zero'
  const [gradeBand, setGradeBand] = useState("all"); // 'all' | '0-5' | '6-8' | '9-12'
  const [houseChoice, setHouseChoice] = useState("ALL"); // 'ALL' | one of HOUSE_LIST

  // New: date range (YYYY-MM-DD strings or "")
  const [sinceDate, setSinceDate] = useState("");
  const [untilDate, setUntilDate] = useState("");

  // 'all' = every educator's awards, 'mine' = only points this educator awarded
  const [awardedBy, setAwardedBy] = useState("all");

  // Column sort state for each table. null = use the mode's default sort.
  const [studentSort, setStudentSort] = useState(null); // { field, dir } | null
  const [eventSort, setEventSort] = useState(null); // { field, dir } | null

  const DESC_DEFAULT_FIELDS = useMemo(() => new Set(["points", "date"]), []);

  const toggleSort = useCallback(
    (effective, setter, field) => {
      setter(() => {
        if (effective.field === field) {
          return { field, dir: effective.dir === "asc" ? "desc" : "asc" };
        }
        return { field, dir: DESC_DEFAULT_FIELDS.has(field) ? "desc" : "asc" };
      });
    },
    [DESC_DEFAULT_FIELDS]
  );

  // Local validation for date range
  const dateRangeError = useMemo(() => {
    if (!sinceDate || !untilDate) return "";
    // compare strings works for YYYY-MM-DD
    return sinceDate > untilDate ? "Start date must be on or before End date." : "";
  }, [sinceDate, untilDate]);

  const [rawStudents, setRawStudents] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);

  const get = useCallback((obj, path, fallback = undefined) => {
    try {
      return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj) ?? fallback;
    } catch {
      return fallback;
    }
  }, []);

  // Build params object for API calls based on date range + awarded-by filter
  const currentDateParams = useMemo(() => {
    const p = {};
    if (sinceDate) p.since = sinceDate;
    if (untilDate) p.until = untilDate;
    if (awardedBy === "mine") p.awarded_by_me = "true";
    return p;
  }, [sinceDate, untilDate, awardedBy]);

  // Load the report whenever filters change. "Points I've Awarded" is a
  // fundamentally different shape (individual award events, not a
  // per-student roster), so it hits a different endpoint entirely.
  useEffect(() => {
    let alive = true;

    // If invalid range, don't fetch; just clear and surface error
    if (dateRangeError) {
      setErr(dateRangeError);
      setRawStudents([]);
      setRawEvents([]);
      setBusy(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setBusy(true);
      setErr("");
      try {
        if (awardedBy === "mine") {
          setRawStudents([]);
          const r = (await reportsApi.pointsAwarded?.(currentDateParams)) ?? null;
          if (!alive) return;

          if (r && r.status === 401) {
            navigate("/login", { replace: true });
            return;
          }
          if (r && r.ok) {
            const data = r?.data ?? {};
            const events = Array.isArray(data.results) ? data.results : [];
            setRawEvents(events);
          } else {
            setErr(r?.data?.message || r?.data?.detail || "Unable to load report data.");
            setRawEvents([]);
          }
          return;
        }

        setRawEvents([]);
        // Prefer detailed points-by-student endpoint; pass date params if present
        const r = (await reportsApi.pointsByStudent?.(currentDateParams)) ?? null;
        if (!alive) return;

        if (r && r.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        if (r && r.ok) {
          const data = r?.data ?? {};
          const students =
            (Array.isArray(data.results) && data.results) ||
            (Array.isArray(data.students) && data.students) ||
            (Array.isArray(data.data) && data.data) ||
            [];
          setRawStudents(students);
        } else {
          // Fallback to summary (also supply date params if supported)
          const res = await reportsApi.summary?.(currentDateParams);
          if (!alive) return;
          if (res?.status === 401) {
            navigate("/login", { replace: true });
            return;
          }
          if (!res?.ok) {
            setErr(res?.data?.message || "Unable to load report data.");
            setRawStudents([]);
          } else {
            const data = res?.data || {};
            const students =
              (Array.isArray(data.students) && data.students) ||
              (Array.isArray(data.results) && data.results) ||
              (Array.isArray(data.data) && data.data) ||
              [];
            setRawStudents(students);
          }
        }
      } catch {
        setErr("Unable to load report data.");
        setRawStudents([]);
        setRawEvents([]);
      } finally {
        if (alive) setBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate, currentDateParams, dateRangeError, awardedBy]);

  const normalizeStudent = useCallback(
    (s) => {
      const id = s?.id ?? s?.scholar_id ?? s?.student_id ?? s?.pk ?? s?.uuid ?? null;

      const first = s?.first_name ?? s?.firstName ?? get(s, "student.first_name", "") ?? "";
      const last = s?.last_name ?? s?.lastName ?? get(s, "student.last_name", "") ?? "";
      const full =
        s?.name ??
        s?.full_name ??
        get(s, "student.name") ??
        [first, last].filter(Boolean).join(" ").trim();
      const { first: sortFirst, last: sortLast } = splitNameParts({ first, last, full });

      const houseNameRaw =
        s?.house_name ??
        (typeof s?.house === "string" ? s.house : undefined) ??
        (typeof s?.house === "object" ? s?.house?.name : undefined) ??
        s?.house ??
        get(s, "student.house_name") ??
        "";

      const points =
        (typeof s?.total_points === "number" ? s?.total_points : undefined) ??
        (typeof s?.points === "number" ? s?.points : undefined) ??
        (typeof s?.score === "number" ? s?.score : undefined) ??
        0;

      const rawGrade =
        s?.grade ??
        s?.grade_level ??
        s?.gradeLevel ??
        s?.grade_name ??
        s?.year ??
        s?.class_year ??
        s?.student_grade ??
        get(s, "student.grade") ??
        get(s, "student.grade_level") ??
        get(s, "profile.grade") ??
        get(s, "profile.grade_level") ??
        get(s, "student.profile.grade") ??
        "";

      // Lunch number: check common fields and nested locations; keep as string to preserve leading zeros
      const lunchRaw =
        s?.lunch_number ??
        s?.lunchNumber ??
        s?.lunch ??
        get(s, "student.lunch_number") ??
        get(s, "student.lunchNumber") ??
        get(s, "student.lunch") ??
        get(s, "profile.lunch_number") ??
        get(s, "profile.lunchNumber") ??
        get(s, "profile.lunch") ??
        "";

      const gradeNumber = normalizeGrade(rawGrade);
      const gradeDisplay =
        gradeNumber !== null ? String(gradeNumber) : rawGrade ? String(rawGrade) : "";

      const lunchNumber = normalizeLunchNumber(lunchRaw);

      return {
        id,
        name: full || `#${id ?? ""}`,
        houseName: houseNameRaw ? String(houseNameRaw).trim() : "",
        points: Number(points) || 0,
        gradeNumber,
        gradeDisplay,
        lunchNumber,
        sortLast: (sortLast || "").toLowerCase(),
        sortFirst: (sortFirst || "").toLowerCase(),
      };
    },
    [get]
  );

  // Normalize + de-duplicate (prefer higher points)
  const students = useMemo(() => {
    const arr = Array.isArray(rawStudents) ? rawStudents.map(normalizeStudent) : [];
    const byKey = new Map();
    for (const s of arr) {
      const key =
        s.id != null
          ? `id:${s.id}`
          : `name:${(s.name || "").trim().toLowerCase()}|house:${normHouse(s.houseName)}`;
      const prev = byKey.get(key);
      if (!prev || (Number(s.points) || 0) > (Number(prev.points) || 0)) {
        byKey.set(key, s);
      }
    }
    return Array.from(byKey.values());
  }, [rawStudents, normalizeStudent]);

  // Apply radio-group filters
  const toPoints = useCallback((v) => (typeof v === "number" ? v : Number(v)) || 0, []);

  const alphaByName = useCallback(
    (a, b) => a.sortLast.localeCompare(b.sortLast) || a.sortFirst.localeCompare(b.sortFirst),
    []
  );

  const byPointsDescThenAlpha = useCallback(
    (a, b) => {
      const ap = toPoints(a.points);
      const bp = toPoints(b.points);
      if (bp !== ap) return bp - ap;
      return alphaByName(a, b);
    },
    [alphaByName, toPoints]
  );

  const inRange = useCallback((g, lo, hi) => g != null && g >= lo && g <= hi, []);

  const studentFieldValue = useCallback(
    (r, field) => {
      switch (field) {
        case "name":
          return `${r.sortLast} ${r.sortFirst}`;
        case "house":
          return normHouse(r.houseName);
        case "grade":
          return r.gradeNumber ?? -1;
        case "points":
          return toPoints(r.points);
        default:
          return "";
      }
    },
    [toPoints]
  );

  const effectiveStudentSort = useMemo(
    () => studentSort || (pointsMode === "top" ? { field: "points", dir: "desc" } : { field: "name", dir: "asc" }),
    [studentSort, pointsMode]
  );
  const onSortStudent = useCallback(
    (field) => toggleSort(effectiveStudentSort, setStudentSort, field),
    [effectiveStudentSort, toggleSort]
  );

  const visibleRows = useMemo(() => {
    let rows = [...students];

    // Grade band
    switch (gradeBand) {
      case "0-5":
        rows = rows.filter((r) => inRange(r.gradeNumber, 0, 5));
        break;
      case "6-8":
        rows = rows.filter((r) => inRange(r.gradeNumber, 6, 8));
        break;
      case "9-12":
        rows = rows.filter((r) => inRange(r.gradeNumber, 9, 12));
        break;
      default:
        // all
        break;
    }

    // House (fixed list)
    if (houseChoice !== "ALL") {
      const wanted = normHouse(houseChoice);
      rows = rows.filter((r) => normHouse(r.houseName) === wanted);
    }

    // Points mode: filter only - the final sort (below) honors whichever
    // column header was clicked, defaulting sensibly per mode.
    if (pointsMode === "zero") {
      rows = rows.filter((r) => toPoints(r.points) === 0);
    } else if (pointsMode === "top") {
      rows = [...rows].sort(byPointsDescThenAlpha).slice(0, TOP_N);
    }

    const { field, dir } = effectiveStudentSort;
    rows = [...rows].sort((a, b) => {
      const av = studentFieldValue(a, field);
      const bv = studentFieldValue(b, field);
      const cmp =
        typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [students, pointsMode, gradeBand, houseChoice, byPointsDescThenAlpha, inRange, toPoints, effectiveStudentSort, studentFieldValue]);

  const normalizeEvent = useCallback((e) => {
    const gradeNumber = normalizeGrade(e?.grade);
    const gradeDisplay = gradeNumber !== null ? String(gradeNumber) : e?.grade ? String(e.grade) : "";
    return {
      id: e?.id ?? null,
      createdAt: e?.created_at ?? "",
      scholarName: e?.scholar_name || "",
      houseName: e?.house || "",
      gradeNumber,
      gradeDisplay,
      habit: e?.habit || "",
      reason: e?.reason || "",
      points: Number(e?.value) || 0,
    };
  }, []);

  const events = useMemo(
    () => (Array.isArray(rawEvents) ? rawEvents.map(normalizeEvent) : []),
    [rawEvents, normalizeEvent]
  );

  const eventFieldValue = useCallback((r, field) => {
    switch (field) {
      case "date":
        return r.createdAt || "";
      case "scholar":
        return (r.scholarName || "").toLowerCase();
      case "house":
        return normHouse(r.houseName);
      case "grade":
        return r.gradeNumber ?? -1;
      case "habit":
        return (r.habit || "").toLowerCase();
      case "reason":
        return (r.reason || "").toLowerCase();
      case "points":
        return r.points;
      default:
        return "";
    }
  }, []);

  const effectiveEventSort = useMemo(() => eventSort || { field: "date", dir: "desc" }, [eventSort]);
  const onSortEvent = useCallback(
    (field) => toggleSort(effectiveEventSort, setEventSort, field),
    [effectiveEventSort, toggleSort]
  );

  // "Points I've Awarded" view: individual events, filtered by the same
  // Grade/House pickers (points mode doesn't apply to a raw event log),
  // sorted by whichever column header was clicked (newest-first by default).
  const visibleEventRows = useMemo(() => {
    let rows = [...events];

    switch (gradeBand) {
      case "0-5":
        rows = rows.filter((r) => inRange(r.gradeNumber, 0, 5));
        break;
      case "6-8":
        rows = rows.filter((r) => inRange(r.gradeNumber, 6, 8));
        break;
      case "9-12":
        rows = rows.filter((r) => inRange(r.gradeNumber, 9, 12));
        break;
      default:
        break;
    }

    if (houseChoice !== "ALL") {
      const wanted = normHouse(houseChoice);
      rows = rows.filter((r) => normHouse(r.houseName) === wanted);
    }

    const { field, dir } = effectiveEventSort;
    rows.sort((a, b) => {
      const av = eventFieldValue(a, field);
      const bv = eventFieldValue(b, field);
      const cmp =
        typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [events, gradeBand, houseChoice, inRange, effectiveEventSort, eventFieldValue]);

  function exportCsv() {
    if (awardedBy === "mine") {
      const headers = ["Date", "Scholar", "House", "Grade", "Habit", "Reason", "Points"];
      const lines = visibleEventRows.map((r) =>
        [
          csvSafe(r.createdAt),
          csvSafe(r.scholarName),
          csvSafe(r.houseName || ""),
          csvSafe(r.gradeDisplay || ""),
          csvSafe(r.habit || ""),
          csvSafe(r.reason || ""),
          csvSafe(r.points ?? 0),
        ].join(",")
      );
      const csv = [headers.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const dateSuffix =
        sinceDate || untilDate
          ? `_from_${(sinceDate || "…").replaceAll("-", "")}_to_${(untilDate || "…").replaceAll("-", "")}`
          : "";
      a.download = `points_awarded_by_me_${gradeBand}_${houseChoice}${dateSuffix}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      return;
    }
    exportStudentCsv();
  }

  function exportStudentCsv() {
    // Added "Lunch Number" column
    const headers = ["Name", "Lunch Number", "House", "Grade", "Points"];
    const lines = visibleRows.map((r) =>
      [
        csvSafe(r.name),
        csvSafe(r.lunchNumber || ""),
        csvSafe(r.houseName || ""),
        csvSafe(r.gradeDisplay || ""),
        csvSafe(r.points ?? 0),
      ].join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);

    // Add date suffix if present
    const dateSuffix =
      sinceDate || untilDate
        ? `_from_${(sinceDate || "…").replaceAll("-", "")}_to_${(untilDate || "…").replaceAll("-", "")}`
        : "";

    a.download = `scholars_${pointsMode}_${gradeBand}_${houseChoice}${dateSuffix}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function csvSafe(val) {
    const s = `${val ?? ""}`;
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  function resetAll() {
    setPointsMode("all");
    setGradeBand("all");
    setHouseChoice("ALL");
    setSinceDate("");
    setUntilDate("");
    setAwardedBy("all");
    setStudentSort(null);
    setEventSort(null);
    setErr("");
  }

  // Styled radio group (bold title + pill options)
  const RadioGroup = ({ label, name, value, onChange, options }) => {
    const optionBaseStyle = {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 9999,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      cursor: "pointer",
      userSelect: "none",
      fontSize: 14,
      lineHeight: 1.2,
    };
    const optionCheckedStyle = {
      border: "1px solid #059669",
      background: "#d1fae5",
      fontWeight: 600,
    };
    const sectionTitleStyle = { fontWeight: 700, color: "#1f2937" };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={sectionTitleStyle}>{label}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {options.map((opt) => {
            const checked = value === opt.value;
            return (
              <label
                key={opt.value}
                style={{ ...(optionBaseStyle), ...(checked ? optionCheckedStyle : null) }}
              >
                <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={(e) => onChange(e.target.value)}
                  style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  // Simple inline field label
  const FieldLabel = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{children}</div>
  );

  // Clickable, sortable column header
  const SortableTh = ({ label, field, sort, onSort, align = "left" }) => {
    const active = sort && sort.field === field;
    const arrow = active ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
    return (
      <th
        onClick={() => onSort(field)}
        title="Click to sort"
        style={{
          textAlign: align,
          background: "#F5F8FB",
          color: "#003e73",
          padding: "10px 12px",
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          position: "sticky",
          top: 0,
          zIndex: 1,
          fontWeight: 700,
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {arrow}
      </th>
    );
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Reports</h1>

      {/* Date range filter */}
      <div
        className="mb-4"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: 12,
          alignItems: "end",
          maxWidth: 520,
        }}
      >
        <div>
          <FieldLabel>Start date</FieldLabel>
          <input
            type="date"
            value={sinceDate}
            onChange={(e) => setSinceDate(e.target.value)}
            className="border rounded px-2 py-2 w-full"
            disabled={busy}
          />
        </div>
        <div>
          <FieldLabel>End date</FieldLabel>
          <input
            type="date"
            value={untilDate}
            onChange={(e) => setUntilDate(e.target.value)}
            className="border rounded px-2 py-2 w-full"
            disabled={busy}
            min={sinceDate || undefined}
          />
        </div>
        <div style={{ alignSelf: "center" }}>
          <button
            className="border rounded px-3 py-2"
            onClick={() => {
              setSinceDate("");
              setUntilDate("");
            }}
            disabled={busy || (!sinceDate && !untilDate)}
            title="Clear dates"
          >
            Clear dates
          </button>
        </div>
      </div>

      {dateRangeError && (
        <div className="mb-3 bg-red-50 text-red-700 border border-red-200 rounded p-3">
          {dateRangeError}
        </div>
      )}

      {/* Filters: radio styling + separators */}
      <div className="grid grid-cols-1 gap-2 mb-4">
        {awardedBy !== "mine" && (
          <>
            <RadioGroup
              label="Select Scholars by Points"
              name="pointsMode"
              value={pointsMode}
              onChange={setPointsMode}
              options={[
                { value: "all", label: "All Scholars" },
                { value: "top", label: "Top Scholars" },
                { value: "zero", label: "Scholars with 0 points" },
              ]}
            />
            <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: "6px 0" }} />
          </>
        )}
        <RadioGroup
          label="Grade Bands"
          name="gradeBand"
          value={gradeBand}
          onChange={setGradeBand}
          options={[
            { value: "all", label: "All Grades" },
            { value: "0-5", label: "Grades 0–5" },
            { value: "6-8", label: "Grades 6–8" },
            { value: "9-12", label: "Grades 9–12" },
          ]}
        />
        <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: "6px 0" }} />
        <RadioGroup
          label="House"
          name="houseChoice"
          value={houseChoice}
          onChange={setHouseChoice}
          options={[
            { value: "ALL", label: "All Houses" },
            ...HOUSE_LIST.map((h) => ({ value: h, label: h })),
          ]}
        />
        <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: "6px 0" }} />
        <RadioGroup
          label="Awarded By"
          name="awardedBy"
          value={awardedBy}
          onChange={setAwardedBy}
          options={[
            { value: "all", label: "All Educators" },
            { value: "mine", label: "Points I've Awarded" },
          ]}
        />

        {/* RIGHT-ALIGNED ACTION BUTTONS */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "24px" }}>
          <button className="border rounded px-3 py-2" onClick={resetAll} disabled={busy}>
            Reset
          </button>
          <button
            className="border rounded px-3 py-2 bg-emerald-600 text-white"
            onClick={exportCsv}
            disabled={busy || !!err || !!dateRangeError}
          >
            Export CSV
          </button>
        </div>
      </div>

      {busy && <div className="mb-3 text-gray-600">Loading…</div>}
      {err && !dateRangeError && (
        <div className="mb-3 bg-red-50 text-red-700 border border-red-200 rounded p-3">
          {err}
        </div>
      )}

      {!busy && !err && !dateRangeError && awardedBy === "mine" && (
        <div className="overflow-auto border rounded">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 14,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "16rem" }} /> {/* Date */}
              <col /> {/* Scholar */}
              <col style={{ width: "8rem" }} /> {/* House */}
              <col style={{ width: "6rem" }} /> {/* Grade */}
              <col style={{ width: "10rem" }} /> {/* Habit */}
              <col style={{ width: "16rem" }} /> {/* Reason */}
              <col style={{ width: "6rem" }} /> {/* Points */}
            </colgroup>
            <thead>
              <tr>
                <SortableTh label="Date" field="date" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="Scholar" field="scholar" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="House" field="house" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="Grade" field="grade" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="Habit" field="habit" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="Reason" field="reason" sort={effectiveEventSort} onSort={onSortEvent} />
                <SortableTh label="Points" field="points" sort={effectiveEventSort} onSort={onSortEvent} align="right" />
              </tr>
            </thead>
            <tbody>
              {visibleEventRows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "10px 12px" }}>
                    You haven't awarded any points matching the current filters.
                  </td>
                </tr>
              ) : (
                visibleEventRows.map((r, i) => {
                  const rowKey = r.id != null ? `id:${r.id}` : `row:${i}`;
                  const dateDisplay = r.createdAt ? new Date(r.createdAt).toLocaleString() : "—";
                  return (
                    <tr key={rowKey}>
                      <td style={{ padding: "10px 12px" }}>{dateDisplay}</td>
                      <td style={{ padding: "10px 12px" }}>{r.scholarName || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{r.houseName || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{r.gradeDisplay || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{r.habit || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{r.reason || "—"}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.points ?? 0}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!busy && !err && !dateRangeError && awardedBy !== "mine" && (
        <div className="overflow-auto border rounded">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 14,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col /> {/* Name */}
              <col style={{ width: "8rem" }} /> {/* House */}
              <col style={{ width: "6rem" }} /> {/* Grade */}
              <col style={{ width: "8rem" }} /> {/* Points */}
            </colgroup>
            <thead>
              <tr>
                <SortableTh label="Name" field="name" sort={effectiveStudentSort} onSort={onSortStudent} />
                <SortableTh label="House" field="house" sort={effectiveStudentSort} onSort={onSortStudent} />
                <SortableTh label="Grade" field="grade" sort={effectiveStudentSort} onSort={onSortStudent} />
                <SortableTh label="Points" field="points" sort={effectiveStudentSort} onSort={onSortStudent} align="right" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "10px 12px" }}>
                    No scholars match the current filters.
                  </td>
                </tr>
              ) : (
                visibleRows.map((r, i) => {
                  const baseKey =
                    r.id != null
                      ? `id:${r.id}`
                      : `name:${(r.name || "").trim().toLowerCase()}|house:${normHouse(r.houseName)}`;
                  const rowKey = `${baseKey}::row:${i}`;
                  return (
                    <tr key={rowKey}>
                      <td style={{ padding: "10px 12px" }}>{r.name}</td>
                      <td style={{ padding: "10px 12px" }}>{r.houseName || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{r.gradeDisplay || "—"}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>
                        {r.points ?? 0}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
